/**
 * Royalty statement status + money helpers.
 *
 * Bulk-create returns statements as `pending`; a background task then moves
 * each one `pending` → `generating` → `draft` (or `error`), filling in the
 * PDF and money fields along the way. Ledger mutations can also flip a
 * `draft` back to `pending` to regenerate it.
 */
import { formatCurrencyFromString, toNumber } from "@/lib/helpers";
import { ROYALTY_STATEMENT_MESSAGES } from "@/lib/trackMessages";
import type { RoyaltyStatement, RoyaltyStatementStatus } from "@/types";

/**
 * Statuses still moving through the generation pipeline. `draft` and `error`
 * are terminal (an errored generation must not keep polling alive).
 */
export function isRoyaltyStatementGenerating(status: RoyaltyStatementStatus): boolean {
  return status === "pending" || status === "generating";
}

/**
 * Money display for statement value / balance. While generating, the backend
 * still holds the default 0 — indistinguishable from a real zero by value
 * alone — so it is masked with "-".
 */
export function formatRoyaltyStatementAmount(
  amount: string,
  currency: string,
  locale: string,
  status: RoyaltyStatementStatus,
): string {
  if (isRoyaltyStatementGenerating(status)) return "-";
  return formatCurrencyFromString(amount, currency, locale);
}

export interface RoyaltyBulkSelectionSummary {
  count: number;
  /** Sum of statement balances; null when currencies are mixed or a statement is still generating. */
  sum: number | null;
  currency: string | null;
  /** Every selected statement can be deleted together (none sent/paid). */
  canDeleteAll: boolean;
  /** Why the selection can't be deleted, for the disabled-button tooltip. Null when it can. */
  cannotDeleteReason: string | null;
  /** Every selected statement can be marked sent together (all drafts). */
  canMarkSentAll: boolean;
  /** Why the selection can't be marked sent, for the disabled-button tooltip. Null when it can. */
  cannotMarkSentReason: string | null;
  /** Every selected statement can be marked paid together (draft/sent, all meeting threshold). */
  canMarkPaidAll: boolean;
  /** Why the selection can't be marked paid, for the disabled-button tooltip. Null when it can. */
  cannotMarkPaidReason: string | null;
}

/**
 * Summarize a bulk selection for the action bar. Bulk delete is all-or-nothing
 * on the backend — a sent or paid statement rejects the whole batch — so the
 * action is gated on the same rule. Mark-sent only transitions drafts, so it is
 * gated on every selected statement being one (the backend's other send checks
 * are server-only and surface through the request error). Mark-paid accepts
 * drafts (they cross Sent atomically) and sent statements, gated on the
 * backend's approximate meets_payment_threshold flag — its authoritative
 * checks (payout ordering, exact threshold) also surface through the error. The balance sum is
 * omitted for mixed-currency selections and while any statement is generating
 * (its balance still holds the default 0, which would make the sum lie).
 */
export function getRoyaltyBulkSelectionSummary(
  statements: RoyaltyStatement[],
): RoyaltyBulkSelectionSummary {
  const currencies = new Set(statements.map((s) => s.currency));
  const isSingleCurrency = currencies.size === 1;
  const hasGenerating = statements.some((s) => isRoyaltyStatementGenerating(s.status));
  const sum =
    statements.length > 0 && isSingleCurrency && !hasGenerating
      ? statements.reduce((acc, s) => acc + toNumber(s.statement_balance), 0)
      : null;

  const hasFinalized = statements.some((s) => s.status === "sent" || s.status === "paid");
  const cannotDeleteReason = hasFinalized ? ROYALTY_STATEMENT_MESSAGES.bulkDeleteFinalized : null;

  const allDrafts = statements.every((s) => s.status === "draft");
  const cannotMarkSentReason =
    statements.length > 0 && !allDrafts ? ROYALTY_STATEMENT_MESSAGES.bulkMarkSentNotDraft : null;

  const allPayableStatus = statements.every((s) => s.status === "draft" || s.status === "sent");
  const allMeetThreshold = statements.every((s) => s.meets_payment_threshold);
  let cannotMarkPaidReason: string | null = null;
  if (statements.length > 0) {
    if (!allPayableStatus) {
      cannotMarkPaidReason = ROYALTY_STATEMENT_MESSAGES.bulkMarkPaidWrongStatus;
    } else if (!allMeetThreshold) {
      cannotMarkPaidReason = ROYALTY_STATEMENT_MESSAGES.bulkMarkPaidBelowThreshold;
    }
  }

  return {
    count: statements.length,
    sum,
    currency: isSingleCurrency ? [...currencies][0] : null,
    canDeleteAll: statements.length > 0 && !hasFinalized,
    cannotDeleteReason,
    canMarkSentAll: statements.length > 0 && allDrafts,
    cannotMarkSentReason,
    canMarkPaidAll: statements.length > 0 && cannotMarkPaidReason == null,
    cannotMarkPaidReason,
  };
}

/**
 * Which contracts a bulk run covers, expressed relative to a baseline the way
 * the preview/checks/bulk-create endpoints resolve it:
 * - `exclude` (default): every eligible contract minus the unticked ones;
 * - `include` (after "deselect all"): only the ticked ones.
 * The mode is fixed at construction; toggling only edits the set. Because the
 * `exclude` baseline never needs the eligible set enumerated client-side, the
 * selection stays exact beyond the loaded page.
 */
export interface ContractSelection {
  mode: "exclude" | "include";
  contracts: ReadonlySet<string>;
}

export const selectAllContracts = (): ContractSelection => ({
  mode: "exclude",
  contracts: new Set(),
});
export const deselectAllContracts = (): ContractSelection => ({
  mode: "include",
  contracts: new Set(),
});

/** Exactly these contracts — e.g. the single-contract form's picked contract. */
export const selectOnlyContracts = (ids: string[]): ContractSelection => ({
  mode: "include",
  contracts: new Set(ids),
});

/** Explicit empty selection: nothing to run, and nothing the API can be asked about. */
export const isEmptySelection = (selection: ContractSelection): boolean =>
  selection.mode === "include" && selection.contracts.size === 0;

export function isContractSelected(selection: ContractSelection, contractId: string): boolean {
  const listed = selection.contracts.has(contractId);
  return selection.mode === "exclude" ? !listed : listed;
}

export function toggleContractSelection(
  selection: ContractSelection,
  contractId: string,
): ContractSelection {
  const contracts = new Set(selection.contracts);
  if (contracts.has(contractId)) contracts.delete(contractId);
  else contracts.add(contractId);
  return { ...selection, contracts };
}

/** Query/body params naming the selection for the preview and bulk-create endpoints. */
export function contractSelectionParams(
  selection: ContractSelection,
): { contract_ids: string[] } | { deselected_contract_ids: string[] } {
  const ids = [...selection.contracts].sort();
  if (selection.mode === "include") return { contract_ids: ids };
  return { deselected_contract_ids: ids };
}
